import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { vertex360CopilotActions } from "store/actions";
import {
  getVertex360CopilotActiveFilters,
  getVertex360CopilotContextSummary,
  getVertex360CopilotConversationId,
  getVertex360CopilotHasConversation,
  getVertex360CopilotHasMoreOlder,
  getVertex360CopilotIsAssistantTyping,
  getVertex360CopilotIsLoadingOlder,
  getVertex360CopilotIsStreaming,
  getVertex360CopilotMessages,
  getVertex360CopilotStreamingMessageId,
} from "store/selector/vertex360Copilot.selector";

const STREAM_IDLE_COMPLETE_MS = 1400;

/**
 * Redux-backed chat bridge for Vertex360 Copilot UI.
 * Domain state lives in vertex360Copilot slice; scroll ref stays local (DOM).
 */
export const useVertex360CopilotChat = () => {
  const dispatch = useDispatch();
  const scrollAnchorRef = useRef(null);
  const scrollRestoreRef = useRef(null);

  const messages = useSelector(getVertex360CopilotMessages);
  const hasConversation = useSelector(getVertex360CopilotHasConversation);
  const conversationId = useSelector(getVertex360CopilotConversationId);
  const isAssistantTyping = useSelector(getVertex360CopilotIsAssistantTyping);
  const isStreaming = useSelector(getVertex360CopilotIsStreaming);
  const streamingMessageId = useSelector(getVertex360CopilotStreamingMessageId);
  const isLoadingOlder = useSelector(getVertex360CopilotIsLoadingOlder);
  const hasMoreOlder = useSelector(getVertex360CopilotHasMoreOlder);
  const activeFilters = useSelector(getVertex360CopilotActiveFilters);
  const contextSummary = useSelector(getVertex360CopilotContextSummary);
  const streamingMessage = messages.find(
    (message) => String(message.id) === String(streamingMessageId ?? "")
  );
  const streamingContentLength = String(streamingMessage?.content || "").length;

  const sendMessage = useCallback(
    (rawText, options) => {
      const text = String(rawText || "").trim();
      if (!text || isAssistantTyping || isStreaming) return false;

      // Free-typed topic change: dismiss unanswered clarify chips in the UI so
      // the user is not trapped on a stale role/person prompt while the new
      // ask is processed server-side (abandon pending clarify).
      const isClarifyAnswer =
        options?.clarifyMessageId ||
        options?.clarifyAnswers ||
        options?.clarifySelectedOption ||
        /^\s*clarification\s*:/i.test(text);
      if (!isClarifyAnswer) {
        const openClarify = [...messages]
          .reverse()
          .find(
            (m) =>
              m?.role === "assistant" &&
              m?.render?.type === "clarify" &&
              !m?.render?.selected &&
              !m?.render?.answers
          );
        if (openClarify?.id) {
          dispatch(
            vertex360CopilotActions.setVertex360CopilotMessageRender(
              openClarify.id,
              {
                render: {
                  ...(openClarify.render || {}),
                  dismissed: true,
                  selected: "__abandoned__",
                },
              }
            )
          );
        }
      }

      dispatch(
        vertex360CopilotActions.sendVertex360CopilotMessageRequest(text, options)
      );
      return true;
    },
    [dispatch, isAssistantTyping, isStreaming, messages]
  );

  const clearActiveFilters = useCallback(() => {
    if (isAssistantTyping || isStreaming) return false;
    dispatch(
      vertex360CopilotActions.setVertex360CopilotActiveFilters({
        activeFilters: null,
        contextSummary: null,
      })
    );
    return sendMessage("Clear the active filters for this conversation.", {
      clearActiveFilters: true,
    });
  }, [dispatch, isAssistantTyping, isStreaming, sendMessage]);

  const stopGeneration = useCallback(() => {
    dispatch(vertex360CopilotActions.stopVertex360CopilotGeneration());
  }, [dispatch]);

  const loadOlderMessages = useCallback(() => {
    if (isLoadingOlder || !hasMoreOlder) return;

    const listEl = scrollAnchorRef.current;
    scrollRestoreRef.current = {
      previousScrollHeight: listEl?.scrollHeight || 0,
      previousScrollTop: listEl?.scrollTop || 0,
    };

    dispatch(vertex360CopilotActions.loadVertex360CopilotOlderMessagesRequest());
  }, [dispatch, hasMoreOlder, isLoadingOlder]);

  const retryMessage = useCallback(
    (rawText) => {
      const text = String(rawText || "").trim();
      if (!text || isAssistantTyping || isStreaming) return false;

      dispatch(vertex360CopilotActions.retryVertex360CopilotMessageRequest(text));
      return true;
    },
    [dispatch, isAssistantTyping, isStreaming]
  );

  const resetConversation = useCallback(() => {
    dispatch(vertex360CopilotActions.stopVertex360CopilotGeneration());
    dispatch(vertex360CopilotActions.resetVertex360CopilotConversation());
  }, [dispatch]);

  const selectClarifyOption = useCallback(
    (message, optionId, selectedLabel, answers) => {
      if (!message || (!optionId && !answers)) return null;
      if (message.render?.selected || message.render?.answers) return null;

      const nextRender = { ...(message.render || {}) };
      if (optionId && optionId !== "__typed__") {
        nextRender.selected = optionId;
      }
      if (selectedLabel) {
        nextRender.selected_label = String(selectedLabel).trim();
      }
      if (answers && typeof answers === "object") {
        nextRender.answers = answers;
      }

      dispatch(
        vertex360CopilotActions.setVertex360CopilotMessageRender(message.id, {
          render: nextRender,
        })
      );

      // Prefer numeric server id (stream complete / history); client temp ids
      // are UUIDs and must not be sent as clarify_message_id.
      const serverMessageId =
        message.serverId ||
        (/^\d+$/.test(String(message.id)) ? String(message.id) : null);
      return serverMessageId;
    },
    [dispatch]
  );

  const rateMessageFeedback = useCallback(
    (messageId, feedbackRating) => {
      if (!messageId) return;
      dispatch(
        vertex360CopilotActions.setVertex360CopilotMessageFeedback(
          messageId,
          feedbackRating
        )
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (isLoadingOlder || !scrollRestoreRef.current) return undefined;

    const { previousScrollHeight, previousScrollTop } = scrollRestoreRef.current;
    scrollRestoreRef.current = null;

    const frame = requestAnimationFrame(() => {
      const listEl = scrollAnchorRef.current;
      if (!listEl) return;
      const newScrollHeight = listEl.scrollHeight;
      listEl.scrollTop =
        newScrollHeight - previousScrollHeight + previousScrollTop;
    });

    return () => cancelAnimationFrame(frame);
  }, [isLoadingOlder, messages]);

  useEffect(() => {
    if (!isStreaming || !streamingMessageId || streamingContentLength === 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      dispatch(vertex360CopilotActions.setVertex360CopilotTyping(false));
      dispatch(
        vertex360CopilotActions.setVertex360CopilotStreaming({
          isStreaming: false,
          streamingMessageId: null,
        })
      );
    }, STREAM_IDLE_COMPLETE_MS);

    return () => window.clearTimeout(timer);
  }, [dispatch, isStreaming, streamingMessageId, streamingContentLength]);

  return {
    messages,
    hasConversation,
    conversationId,
    isAssistantTyping,
    isStreaming,
    streamingMessageId,
    isLoadingOlder,
    hasMoreOlder,
    activeFilters,
    contextSummary,
    scrollAnchorRef,
    sendMessage,
    clearActiveFilters,
    retryMessage,
    selectClarifyOption,
    rateMessageFeedback,
    loadOlderMessages,
    resetConversation,
    stopGeneration,
  };
};
